<script>
import countries from 'shared/constants/countries.js';
import parsePhoneNumber from 'libphonenumber-js';

const DEFAULT_COUNTRY_CODE = 'RU';
const DEFAULT_DIAL_CODE = '+7';
const PRIORITY_COUNTRY_IDS = [
  'RU',
  'KZ',
  'UZ',
  'KG',
  'BY',
  'AM',
  'AZ',
  'GE',
  'TJ',
  'MD',
  'UA',
];
const COUNTRY_SEARCH_ALIASES = {
  RU: ['rf', 'russia', 'rossiya', 'россия', 'рф'],
  KZ: ['kazakhstan', 'kazahstan', 'казахстан'],
  UZ: ['uzbekistan', 'узбекистан'],
  KG: ['kyrgyzstan', 'kirgizstan', 'киргизия', 'кыргызстан'],
};
const priorityCountryRank = countryId => {
  const index = PRIORITY_COUNTRY_IDS.indexOf(
    `${countryId || ''}`.toUpperCase()
  );
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export default {
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['blur', 'setCode', 'update:modelValue'],
  data() {
    return {
      selectedIndex: -1,
      showDropdown: false,
      searchCountry: '',
      activeCountryCode: DEFAULT_COUNTRY_CODE,
      activeDialCode: DEFAULT_DIAL_CODE,
      phoneNumber: this.modelValue,
    };
  },
  computed: {
    prioritizedCountries() {
      return [...countries].sort((countryA, countryB) => {
        const countryARank = priorityCountryRank(countryA.id);
        const countryBRank = priorityCountryRank(countryB.id);

        if (countryARank !== countryBRank) {
          return countryARank - countryBRank;
        }

        return 0;
      });
    },
    countries() {
      return [
        {
          name: this.dropdownFirstItemName,
          dial_code: '',
          emoji: '',
          id: '',
        },
        ...this.prioritizedCountries,
      ];
    },
    dropdownFirstItemName() {
      return this.activeCountryCode ? 'Clear selection' : 'Select Country';
    },
    filteredCountriesBySearch() {
      const search = this.normalizeCountrySearch(this.searchCountry);

      return this.countries.filter(country => {
        return this.countryMatchesSearch(country, search);
      });
    },
    activeCountry() {
      if (this.activeCountryCode) {
        return this.countries.find(
          country => this.countryId(country) === this.activeCountryCode
        );
      }
      return '';
    },
  },
  watch: {
    modelValue(newValue) {
      this.syncCountryStateFromModel(newValue);
    },
  },
  mounted() {
    this.setActiveCountry();
  },
  methods: {
    onOutsideClick(e) {
      if (
        this.showDropdown &&
        e.target !== this.$refs.dropdown &&
        !this.$refs.dropdown.contains(e.target)
      ) {
        this.closeDropdown();
      }
    },
    onChange(event) {
      const normalizedPhone = this.normalizePhoneInput(event.target.value);
      this.applyNormalizedPhone(normalizedPhone);
      event.target.value = this.phoneNumber;
    },
    onBlur() {
      this.$emit('blur', this.phoneNumber);
    },
    applyNormalizedPhone(
      { countryCode, dialCode, localNumber },
      emitModel = true
    ) {
      if (countryCode) {
        this.activeCountryCode = countryCode;
      }

      if (dialCode) {
        this.activeDialCode = dialCode;
      }

      this.phoneNumber = localNumber;
      if (emitModel) {
        this.$emit('update:modelValue', localNumber);
      }
      this.$emit('setCode', this.activeDialCode);
    },
    countryId(country) {
      return `${country?.id || ''}`.toUpperCase();
    },
    normalizeCountrySearch(value) {
      return `${value || ''}`.trim().toLowerCase();
    },
    countryMatchesSearch(country, search) {
      if (!search) return true;

      const countryId = this.countryId(country);
      const aliases = COUNTRY_SEARCH_ALIASES[countryId] || [];
      const searchableTokens = [
        country.name,
        country.dial_code,
        countryId,
        ...aliases,
      ];

      return searchableTokens.some(token =>
        this.normalizeCountrySearch(token).includes(search)
      );
    },
    normalizeDigits(value) {
      return `${value || ''}`.replace(/[^\d]/g, '');
    },
    findCountryByDialCode(dialCode) {
      const country = this.prioritizedCountries.find(
        candidate => candidate.dial_code === dialCode
      );

      return country ? this.countryId(country) : '';
    },
    normalizeLocalNumber(localNumber, dialCode = this.activeDialCode) {
      let digits = this.normalizeDigits(localNumber);
      const dialDigits = this.normalizeDigits(dialCode);

      if (!digits) return '';

      if (
        dialDigits &&
        digits.startsWith(dialDigits) &&
        digits.length > dialDigits.length + 5
      ) {
        digits = digits.slice(dialDigits.length);
      }

      if (
        dialCode === DEFAULT_DIAL_CODE &&
        digits.length === 11 &&
        ['7', '8'].includes(digits[0])
      ) {
        digits = digits.slice(1);
      }

      return digits;
    },
    normalizePhoneInput(value) {
      const rawValue = `${value || ''}`.trim();

      if (!rawValue) {
        return {
          countryCode: this.activeCountryCode || DEFAULT_COUNTRY_CODE,
          dialCode: this.activeDialCode || DEFAULT_DIAL_CODE,
          localNumber: '',
        };
      }

      if (!rawValue.startsWith('+')) {
        return {
          countryCode: this.activeCountryCode || DEFAULT_COUNTRY_CODE,
          dialCode: this.activeDialCode || DEFAULT_DIAL_CODE,
          localNumber: this.normalizeLocalNumber(rawValue),
        };
      }

      const parsedNumber = this.parsePhone(rawValue);

      if (parsedNumber) {
        const dialCode = parsedNumber.countryCallingCode
          ? `+${parsedNumber.countryCallingCode}`
          : this.activeDialCode || DEFAULT_DIAL_CODE;
        const countryCode =
          parsedNumber.country ||
          this.findCountryByDialCode(dialCode) ||
          this.activeCountryCode ||
          DEFAULT_COUNTRY_CODE;

        return {
          countryCode,
          dialCode,
          localNumber: this.normalizeLocalNumber(
            parsedNumber.nationalNumber,
            dialCode
          ),
        };
      }

      return {
        countryCode: this.activeCountryCode || DEFAULT_COUNTRY_CODE,
        dialCode: this.activeDialCode || DEFAULT_DIAL_CODE,
        localNumber: this.normalizeLocalNumber(rawValue),
      };
    },
    onSearchCountry() {
      this.selectedIndex = 0;
    },
    moveUp() {
      if (!this.showDropdown) return;
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.scrollToSelected();
    },
    moveDown() {
      if (!this.showDropdown) return;
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.filteredCountriesBySearch.length - 1
      );
      this.scrollToSelected();
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const dropdown = this.$refs.dropdown;
        const selectedItem = this.$refs.dropdownItem[this.selectedIndex];
        const dropdownSearchbarHeight = 40;
        if (selectedItem) {
          const selectedItemTop = selectedItem.offsetTop;
          dropdown.scrollTop = selectedItemTop - dropdownSearchbarHeight;
        }
      });
    },
    onSelectCountry(country) {
      if (!country || !this.showDropdown) return;
      this.activeCountryCode = this.countryId(country);
      this.searchCountry = '';
      this.activeDialCode = country.dial_code;
      this.$emit('setCode', country.dial_code);
      this.closeDropdown();
      this.$refs.phoneNumberInput.focus();
    },
    normalizePhoneCandidate(value) {
      const rawValue = `${value || ''}`.trim();
      if (!rawValue) {
        return '';
      }

      if (rawValue.startsWith('+')) {
        return rawValue;
      }

      return rawValue;
    },
    parsePhone(value) {
      const candidate = this.normalizePhoneCandidate(value);
      if (!candidate) {
        return null;
      }

      try {
        return parsePhoneNumber(candidate);
      } catch (error) {
        return null;
      }
    },
    syncCountryStateFromModel(value) {
      const normalizedValue = `${value || ''}`;

      if (!normalizedValue) {
        this.phoneNumber = '';
        this.activeCountryCode = this.activeCountryCode || DEFAULT_COUNTRY_CODE;
        this.activeDialCode = this.activeDialCode || DEFAULT_DIAL_CODE;
        this.$emit('setCode', this.activeDialCode);
        return;
      }

      this.applyNormalizedPhone(this.normalizePhoneInput(normalizedValue), false);
    },
    setActiveCountry() {
      const rawValue = this.modelValue || this.phoneNumber;
      this.syncCountryStateFromModel(rawValue);
    },
    toggleCountryDropdown() {
      this.showDropdown = !this.showDropdown;
      this.selectedIndex = -1;
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.$refs.searchbar.focus();
        });
      }
    },
    closeDropdown() {
      this.selectedIndex = -1;
      this.showDropdown = false;
    },
  },
};
</script>

<template>
  <div class="relative phone-input--wrap">
    <div
      class="flex items-center justify-start border-none outline outline-1 rounded-lg bg-n-alpha-black2"
      :class="
        error
          ? 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 mb-1'
          : 'mb-4 outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6'
      "
    >
      <div
        class="cursor-pointer py-2 pr-1.5 pl-2 rounded-tl-lg rounded-bl-lg flex items-center justify-center gap-1.5 bg-n-solid-3 h-10 w-16 shrink-0"
        @click.prevent="toggleCountryDropdown"
      >
        <h5 v-if="activeCountry" class="mb-0">
          {{ activeCountry.emoji }}
        </h5>
        <fluent-icon v-else icon="globe" class="fluent-icon" size="16" />
        <fluent-icon icon="chevron-down" class="fluent-icon" size="12" />
      </div>
      <span
        v-if="activeDialCode"
        class="flex py-2 ltr:pl-2 rtl:pr-2 text-base font-normal leading-normal text-n-slate-12"
      >
        {{ activeDialCode }}
      </span>
      <input
        ref="phoneNumberInput"
        :value="phoneNumber"
        type="tel"
        class="no-margin !rounded-tl-none !rounded-bl-none !outline-none !border-0 font-normal !w-full !bg-transparent text-base !px-1.5 placeholder:font-normal"
        :placeholder="placeholder"
        :readonly="readonly"
        :style="styles"
        @input="onChange"
        @blur="onBlur"
      />
    </div>
    <div
      v-if="showDropdown"
      ref="dropdown"
      v-on-clickaway="onOutsideClick"
      tabindex="0"
      class="z-10 absolute h-60 w-[12.5rem] shadow-md overflow-y-auto top-10 rounded-lg px-0 pt-0 pb-1 bg-n-alpha-3 backdrop-blur-[100px]"
      @keydown.prevent.up="moveUp"
      @keydown.prevent.down="moveDown"
      @keydown.prevent.enter="
        onSelectCountry(filteredCountriesBySearch[selectedIndex])
      "
    >
      <div
        class="sticky top-0 p-1 bg-white dark:bg-transparent backdrop-blur-[100px]"
      >
        <input
          ref="searchbar"
          v-model="searchCountry"
          type="text"
          :placeholder="$t('GENERAL.PHONE_INPUT.PLACEHOLDER')"
          class="!h-8 !mb-0 !text-sm !outline-n-brand dark:!outline-n-brand"
          @input="onSearchCountry"
        />
      </div>
      <div
        v-for="(country, index) in filteredCountriesBySearch"
        ref="dropdownItem"
        :key="index"
        class="flex items-center px-1 py-0 cursor-pointer h-7 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
        :class="{
          'bg-n-alpha-1 dark:bg-n-alpha-2':
            countryId(country) === activeCountryCode ||
            index === selectedIndex,
        }"
        @click="onSelectCountry(country)"
      >
        <span class="mr-1 text-base">{{ country.emoji }}</span>

        <span
          class="max-w-[7.5rem] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ country.name }}
        </span>
        <span class="ml-1 text-xs text-n-slate-11">
          {{ country.dial_code }}
        </span>
      </div>
      <div v-if="filteredCountriesBySearch.length === 0">
        <span
          class="flex items-center justify-center mt-4 text-sm text-n-slate-10"
        >
          {{ $t('GENERAL.PHONE_INPUT.EMPTY_STATE') }}
        </span>
      </div>
    </div>
  </div>
</template>
